import {useMutation, useQuery} from '@apollo/client'
import {useEffect, useState, FormEvent} from 'react'
import {Button, Modal, Spinner, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'
import moment from 'moment'
import {UPDATE_COMMISSION_REPORT_DETAIL} from '../../../gql/mutations/commissionReportDetailMutations'
import {
  IResUpdateCommissionReportDetail,
  NodeCommissionReportDetail,
} from '../../../types/commissionReportDetailApi'
import {parseId} from '../../../helpers'
import {GET_ALL_STATES_EU_LIST} from '../../../gql/queries/statesEUQuery'

interface IModalEditDetailProps {
  isOpen: boolean
  onClose: () => void
  infoEdit: NodeCommissionReportDetail | null
  refetch: () => void
}

export const ModalEditCommissionReportDetail: React.FC<IModalEditDetailProps> = ({
  isOpen,
  onClose,
  infoEdit,
  refetch,
}) => {
  const [formData, setFormData] = useState({
    client: '',
    commissionType: '',
    memberId: '',
    rate: '',
    members: '',
    commissionReport: '',
    user: '',
    status: '',
    subtotal: 0,
    effectiveMonth: new Date().getMonth() + 1,
    effectiveYear: new Date().getFullYear(),
    paidDate: '',
    stateEU: '',
  })

  // Query para obtener estados
  const { data: statesData } = useQuery<{ allStatesList: { id: string; name: string; code: string }[] }>(GET_ALL_STATES_EU_LIST)

  const [gqlUpdate, {loading, data, error}] = useMutation<IResUpdateCommissionReportDetail>(
    UPDATE_COMMISSION_REPORT_DETAIL
  )

  useEffect(() => {
    if (infoEdit) {
      const month = infoEdit.effectiveMonth ?? new Date().getMonth() + 1
      const year = infoEdit.effectiveYear ?? new Date().getFullYear()
      const paidDate = moment().year(year).month(month - 1).format('YYYY-MM')

      setFormData({
        client: infoEdit.client ?? '',
        commissionType: infoEdit.commissionType ?? 'COMMISSION',
        memberId: infoEdit.memberId ?? '',
        rate: infoEdit.rate ?? 0,
        members: (infoEdit.members ?? 1).toString(),
        commissionReport: infoEdit.commissionReport?.id ?? '',
        user: infoEdit.user?.id ?? '',
        status: infoEdit.status ?? 'PENDING',
        subtotal: typeof infoEdit.subtotal === 'string' ? parseFloat(infoEdit.subtotal) : infoEdit.subtotal ?? 0,
        effectiveMonth: month,
        effectiveYear: year,
        paidDate: paidDate,
        stateEU: infoEdit.stateEU?.id ? String(parseId(infoEdit.stateEU.id)) : '',
      })
    }
  }, [infoEdit])


  useEffect(() => {
    if (!data) return
    const res = data.commissionReportDetailUpdateMutation
    if (res?.success) {
      toast.success('Commission Report Detail updated successfully')
      refetch()
      onClose()
    } else if (res?.errors?.length) {
      toast.error(`Error: ${res.errors[0]?.message}`)
    }
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({...prev, [field]: value}))
  }

  const handleDateChange = (value: string) => {
    const date = moment(value)
    setFormData(prev => ({
      ...prev,
      paidDate: value,
      effectiveMonth: date.month() + 1,
      effectiveYear: date.year(),
    }))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!infoEdit) return

    const {paidDate, stateEU, ...inputData} = formData

    const input: Record<string, unknown> = {
      ...inputData,
      commissionReport: parseId(formData.commissionReport),
      user: parseId(formData.user),
      subtotal: Number(formData.rate) * Number(formData.members),
      effectiveMonth: formData.effectiveMonth,
      effectiveYear: formData.effectiveYear,
    }

    // Solo agregar stateEU si tiene valor
    if (stateEU) {
      input.stateEU = parseId(stateEU)
    }

    gqlUpdate({
      variables: {
        id: parseId(infoEdit.id),
        input,
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Update report record</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label>Client</Form.Label>
            <Form.Control
              type='text'
              value={formData.client}
              onChange={e => handleChange('client', e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Commission Type</Form.Label>
            <Form.Select
              value={formData.commissionType}
              onChange={e => handleChange('commissionType', e.target.value)}
              required
            >
              <option value=''>Select type</option>
              <option value='COMMISSION'>Commission</option>
              <option value='DEDUCTION'>Deduction</option>
              <option value='CHARGEBACK'>Chargeback</option>
              <option value='BONUS'>Bonus</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Member ID</Form.Label>
            <Form.Control
              type='text'
              value={formData.memberId}
              onChange={e => handleChange('memberId', e.target.value)}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>State</Form.Label>
            <Form.Select
              value={formData.stateEU}
              onChange={e => handleChange('stateEU', e.target.value)}
            >
              <option value=''>Select state...</option>
              {statesData?.allStatesList?.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.code} - {state.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Rate (%)</Form.Label>
            <Form.Control
              type='number'
              step='0.01'
              value={formData.rate}
              onChange={e => handleChange('rate', e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Quantity of Members</Form.Label>
            <Form.Control
              type='number'
              value={formData.members}
              onChange={e => handleChange('members', e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Paid Date</Form.Label>
            <Form.Control
              type='month'
              value={formData.paidDate}
              onChange={e => handleDateChange(e.target.value)}
              required
            />
            <Form.Text className='text-muted'>
              This will update the effective month and year
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClose}>
            CLOSE
          </Button>
          <Button type='submit' variant='primary' disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            SAVE
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
